import axios from 'axios';
import type { PatreonReleasePackage, StagedMediaLinks } from '../components/megaphone/patreonStagedRelease';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Send session cookie on every request
});

// Add request interceptor for auth if needed
apiClient.interceptors.request.use(
  (config) => {
    // Add auth token if available
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Attach admin token to admin-gated endpoints. The router-level
    // require_admin_token dependency reads the x-admin-token header,
    // so any new admin-protected path needs to be in this list.
    const url = config.url || '';
    const ADMIN_PATHS = [
      '/api/v1/admin',
      '/api/v1/megaphone/cta-campaigns',
      '/api/v1/megaphone/footer-templates',
      '/api/v1/megaphone/mum',
      '/api/v1/episodes',
      '/api/v1/patreon',
    ];
    if (ADMIN_PATHS.some(p => url.startsWith(p))) {
      const adminToken = localStorage.getItem('adminToken');
      if (adminToken) {
        config.headers['x-admin-token'] = adminToken;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Simple event dispatcher used by the admin login dialog to react to 401s
// without having to couple the interceptor to specific React components.
export const ADMIN_UNAUTHORIZED_EVENT = 'admin-unauthorized';

// Validate an admin token candidate by making a real admin-gated request with
// it. Resolves true on 200 (token — or an existing valid session — is accepted),
// throws on 401 (rejected). Lets the login dialog give the user a definite
// yes/no instead of blindly storing whatever was pasted.
export const verifyAdminToken = async (candidate: string): Promise<boolean> => {
  await apiClient.get('/api/v1/admin/schedule', {
    headers: { 'x-admin-token': candidate },
  });
  return true;
};

// Add response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const url: string = error.config?.url || '';
    const status = error.response?.status;
    if (status === 401) {
      const ADMIN_PATHS_FOR_401 = [
        '/api/v1/admin',
        '/api/v1/megaphone/cta-campaigns',
        '/api/v1/megaphone/footer-templates',
        '/api/v1/megaphone/mum',
        '/api/v1/episodes',
        '/api/v1/patreon',
      ];
      if (ADMIN_PATHS_FOR_401.some(p => url.startsWith(p))) {
        // Only pop the "Admin token required" dialog for a genuine admin-GATE
        // 401 (detail === "admin token required"). Endpoints like
        // /megaphone/mum/setup-cookies also return 401 for BUSINESS reasons
        // ("cookies rejected by Megaphone" — a stale Megaphone session, NOT an
        // admin-auth problem); those must fall through to the calling
        // component's error handler so the user sees the real message instead
        // of being told to fix an admin token that's already fine.
        const detail = String((error.response?.data as any)?.detail || '').toLowerCase();
        if (detail.includes('admin token')) {
          try {
            window.dispatchEvent(new CustomEvent(ADMIN_UNAUTHORIZED_EVENT));
          } catch {
            // no-op in non-browser environments
          }
        }
        // Don't wipe authToken for admin endpoints; admin uses a different token.
      } else {
        // Handle unauthorized for regular app auth
        localStorage.removeItem('authToken');
        // Redirect to login if needed
      }
    }
    return Promise.reject(error);
  }
);

export interface DashboardMetrics {
  kpis: {
    totalReach: number;
    reachChange: number;
    growthRate: number;
    growthChange: number;
    engagement: number;
    engagementChange: number;
    revenue: number;
    revenueChange: number;
  };
  timeline: Array<{
    date: string;
    youtube?: number;
    twitter?: number;
    instagram?: number;
    tiktok?: number;
    facebook?: number;
    spotify?: number;
    total: number;
  }>;
  platforms: Array<{
    platform: string;
    value: number;
    percentage: number;
    growth: number;
  }>;
  sparklines: {
    reach: Array<{ value: number }>;
    growth: Array<{ value: number }>;
    engagement: Array<{ value: number }>;
    revenue: Array<{ value: number }>;
  };
}

export const dashboardAPI = {
  getMetrics: async (timeRange: string): Promise<DashboardMetrics> => {
    const response = await apiClient.get(`/api/v1/dashboard/metrics`, {
      params: { time_range: timeRange }
    });
    return response.data;
  },
  
  getChannelMetrics: async (channelId: string, timeRange: string) => {
    const response = await apiClient.get(`/api/v1/youtube/channels/${channelId}/metrics`, {
      params: { time_range: timeRange }
    });
    return response.data;
  },
  
  getVideoMetrics: async (videoId: string) => {
    const response = await apiClient.get(`/api/v1/youtube/videos/${videoId}/metrics`);
    return response.data;
  },
  
  getRevenueData: async (timeRange: string) => {
    const response = await apiClient.get(`/api/v1/youtube/revenue`, {
      params: { time_range: timeRange }
    });
    return response.data;
  }
};

export const youtubeCalendarAPI = {
  get: async (past_months = 24) =>
    (await apiClient.get('/api/v1/youtube/calendar', { params: { past_months } })).data,
};

export const youtubeAPI = {
  getSummary: async () => {
    const response = await apiClient.get('/api/v1/youtube/summary');
    return response.data;
  },
  
  getKPIs: async () => {
    const response = await apiClient.get('/api/v1/youtube/dashboard/kpis');
    return response.data;
  },
  
  getTimeline: async (days: number = 30) => {
    const response = await apiClient.get('/api/v1/youtube/dashboard/timeline', {
      params: { days }
    });
    return response.data;
  },
  
  getTopVideos: async (metric: string = 'views', limit: number = 10) => {
    const response = await apiClient.get('/api/v1/youtube/top-videos', {
      params: { metric, limit }
    });
    return response.data;
  },
  
  getAnalyticsOverview: async (days: number = 30) => {
    const response = await apiClient.get('/api/v1/youtube/analytics/overview', {
      params: { days }
    });
    return response.data;
  },
  
  getChannelMetrics: async (days: number = 30) => {
    const response = await apiClient.get('/api/v1/youtube/channel/metrics', {
      params: { days }
    });
    return response.data;
  },
  
  getVideos: async (limit: number = 50, offset: number = 0, sortBy: string = 'published_at', order: string = 'desc') => {
    const response = await apiClient.get('/api/v1/youtube/videos', {
      params: { limit, offset, sort_by: sortBy, order }
    });
    return response.data;
  },
  
  getVideoDetail: async (videoId: string) => {
    const response = await apiClient.get(`/api/v1/youtube/videos/${videoId}`);
    return response.data;
  },
  
  getVideoMetrics: async (videoId: string, days: number = 30) => {
    const response = await apiClient.get(`/api/v1/youtube/videos/${videoId}/metrics`, {
      params: { days }
    });
    return response.data;
  },

  getPersonalAnalytics: async (days: number = 30) => {
    const response = await apiClient.get('/api/v1/youtube/personal/analytics', {
      params: { days }
    });
    return response.data;
  },

  getReportingSummary: async (days: number = 30) => {
    const response = await apiClient.get('/api/v1/youtube/reporting/summary', {
      params: { days }
    });
    return response.data;
  },

  getComprehensiveDashboard: async (days: number = 30) => {
    const response = await apiClient.get('/api/v1/youtube/dashboard/comprehensive', {
      params: { days }
    });
    return response.data;
  },

  getAnalyticsSummary: async (days: number = 30) => {
    const response = await apiClient.get('/api/v1/youtube/analytics/summary', {
      params: { days }
    });
    return response.data;
  },

  // Insights
  getEngagementSummary: async (days: number = 60) => {
    const response = await apiClient.get('/api/v1/youtube/insights/engagement-summary', { params: { days } });
    return response.data;
  },
  getCohortsSimple: async (limitMonths: number = 12) => {
    const response = await apiClient.get('/api/v1/youtube/insights/cohorts-simple', { params: { limit_months: limitMonths } });
    return response.data;
  },
  getCadenceHeatmap: async () => {
    const response = await apiClient.get('/api/v1/youtube/insights/cadence-heatmap');
    return response.data;
  },
  getSentiment: async (limitDays: number = 30) => {
    const response = await apiClient.get('/api/v1/youtube/insights/sentiment', { params: { limit_days: limitDays } });
    return response.data;
  },
  getEarlyPerformance: async (days: number = 30, limit: number = 50) => {
    const response = await apiClient.get('/api/v1/youtube/insights/early-performance', { params: { days, limit } });
    return response.data;
  },
  getTopics: async (days: number = 365, minSupport: number = 3, limit: number = 50) => {
    const response = await apiClient.get('/api/v1/youtube/insights/topics', { params: { days, min_support: minSupport, limit } });
    return response.data;
  },
  getDiscoveryFunnel: async (days: number = 30) => {
    const response = await apiClient.get('/api/v1/youtube/insights/impressions-ctr', { params: { days } });
    return response.data;
  },
  getRetentionCurve: async (videoId: string, mode: string = 'auto') => {
    const response = await apiClient.get('/api/v1/youtube/insights/retention/curve', { params: { video_id: videoId, mode } });
    return response.data;
  },
  getRetentionVideos: async (limit: number = 500) => {
    const response = await apiClient.get('/api/v1/youtube/insights/retention/videos', { params: { limit } });
    return response.data;
  },
  getRetentionOverview: async (days: number = 60) => {
    const response = await apiClient.get('/api/v1/youtube/insights/retention/overview', { params: { days } });
    return response.data;
  },
  getPublishRecommendations: async (days: number = 90, limit: number = 5) => {
    const response = await apiClient.get('/api/v1/youtube/insights/publish-recommendations', { params: { days, limit } });
    return response.data;
  },
  getTitleThumbSuggestions: async (days: number = 180) => {
    const response = await apiClient.get('/api/v1/youtube/insights/title-thumbnail-suggestions', { params: { days } });
    return response.data;
  },
  getTrafficSources: async (days: number = 30) => {
    const response = await apiClient.get('/api/v1/youtube/insights/traffic-sources', { params: { days } });
    return response.data;
  },
  getPlaybackLocations: async (days: number = 30) => {
    const response = await apiClient.get('/api/v1/youtube/insights/playback-locations', { params: { days } });
    return response.data;
  },
  getDevicesOS: async (days: number = 30) => {
    const response = await apiClient.get('/api/v1/youtube/insights/devices-os', { params: { days } });
    return response.data;
  },
  getGeography: async (days: number = 30) => {
    const response = await apiClient.get('/api/v1/youtube/insights/geography', { params: { days } });
    return response.data;
  },
  getSearchTerms: async (days: number = 90, topN: number = 50) => {
    const response = await apiClient.get('/api/v1/youtube/insights/search-terms', { params: { days, top_n: topN } });
    return response.data;
  },
  getContentVelocity: async (days: number = 365) => {
    const response = await apiClient.get('/api/v1/youtube/insights/content-velocity', { params: { days } });
    return response.data;
  },
  getAvgViewDuration: async (days: number = 30) => {
    const response = await apiClient.get('/api/v1/youtube/insights/avg-view-duration', { params: { days } });
    return response.data;
  },
  getShortsVsLongform: async (days: number = 365) => {
    const response = await apiClient.get('/api/v1/youtube/insights/shorts-vs-longform', { params: { days } });
    return response.data;
  },
  getImpressionsCtr: async (days: number = 30) => {
    const response = await apiClient.get('/api/v1/youtube/insights/impressions-ctr', { params: { days } });
    return response.data;
  },
  getReturningViewers: async (days: number = 90) => {
    const response = await apiClient.get('/api/v1/youtube/insights/returning-viewers', { params: { days } });
    return response.data;
  }
};

export const adminAPI = {
  getSchedule: async () => {
    const response = await apiClient.get('/api/v1/admin/schedule');
    return response.data;
  },
  getRuns: async (params: { job_name?: string; status?: string; page?: number; per_page?: number } = {}) => {
    const response = await apiClient.get('/api/v1/admin/runs', { params });
    return response.data;
  },
  getOverview: async () => {
    const response = await apiClient.get('/api/v1/admin/overview');
    return response.data;
  },
  getTables: async () => {
    const response = await apiClient.get('/api/v1/admin/tables');
    return response.data;
  },
  getAudit: async (force = false, maxAge = 300) => {
    const response = await apiClient.get('/api/v1/admin/audit', { params: { force, max_age: maxAge } });
    return response.data;
  },
  getLogs: async (lines = 100) => {
    const response = await apiClient.get('/api/v1/admin/logs', { params: { lines } });
    return response.data;
  },
  triggerJob: async (jobName: string) => {
    const response = await apiClient.post(`/api/v1/admin/schedule/${jobName}/trigger`);
    return response.data;
  },
  getRunning: async () => (await apiClient.get('/api/v1/admin/running')).data,
  getErrors: async (p: { resolved?: string; days?: number; page?: number; per_page?: number } = {}) => (await apiClient.get('/api/v1/admin/errors', { params: p })).data,
  resolveError: async (runId: number, note?: string) => (await apiClient.post(`/api/v1/admin/errors/${runId}/resolve`, null, { params: { note } })).data,
  unresolveError: async (runId: number) => (await apiClient.post(`/api/v1/admin/errors/${runId}/unresolve`)).data,
  getActions: async (limit = 200) => (await apiClient.get('/api/v1/admin/actions', { params: { limit } })).data,
  getAlertConfig: async () => (await apiClient.get('/api/v1/admin/alerts/config')).data,
  updateAlertConfig: async (updates: any) => (await apiClient.put('/api/v1/admin/alerts/config', updates)).data,
  getAlertLog: async (limit = 100) => (await apiClient.get('/api/v1/admin/alerts/log', { params: { limit } })).data,
  testAlert: async () => (await apiClient.post('/api/v1/admin/alerts/test')).data,
};

export const ctaCampaignAPI = {
  list: async () => (await apiClient.get('/api/v1/megaphone/cta-campaigns/')).data,
  create: async (data: any) => (await apiClient.post('/api/v1/megaphone/cta-campaigns/', data)).data,
  get: async (id: number) => (await apiClient.get(`/api/v1/megaphone/cta-campaigns/${id}`)).data,
  update: async (id: number, data: any) => (await apiClient.put(`/api/v1/megaphone/cta-campaigns/${id}`, data)).data,
  delete: async (id: number) => (await apiClient.delete(`/api/v1/megaphone/cta-campaigns/${id}`)).data,
  schedule: async (id: number) => (await apiClient.post(`/api/v1/megaphone/cta-campaigns/${id}/schedule`)).data,
  applyNow: async (id: number) => (await apiClient.post(`/api/v1/megaphone/cta-campaigns/${id}/apply-now`)).data,
  stop: async (id: number) => (await apiClient.post(`/api/v1/megaphone/cta-campaigns/${id}/stop`)).data,
  retryFailed: async (id: number) => (await apiClient.post(`/api/v1/megaphone/cta-campaigns/${id}/retry-failed`)).data,
  episodes: async (id: number, page = 1, status?: string) => (await apiClient.get(`/api/v1/megaphone/cta-campaigns/${id}/episodes`, { params: { page, status } })).data,
  audit: async (id: number, limit = 100) => (await apiClient.get(`/api/v1/megaphone/cta-campaigns/${id}/audit`, { params: { limit } })).data,
  preview: async (cta_html: string, marker_id: string) => (await apiClient.get('/api/v1/megaphone/cta-campaigns/preview', { params: { cta_html, marker_id } })).data,
  active: async () => (await apiClient.get('/api/v1/megaphone/cta-campaigns/active')).data,
};

// Competitor SERP rankings via Serper.dev (daily snapshots)
export const seoSerpAPI = {
  keywords: async () =>
    (await apiClient.get('/api/v1/seo/serp-keywords')).data,
  rankings: async (keyword: string, days = 180, category?: string) =>
    (await apiClient.get('/api/v1/seo/serp-rankings',
      { params: { keyword, days, category } })).data,
  summary: async (category?: string) =>
    (await apiClient.get('/api/v1/seo/serp-summary',
      { params: { category } })).data,
  otherDomains: async (keyword: string, days = 30) =>
    (await apiClient.get('/api/v1/seo/serp-other-domains',
      { params: { keyword, days } })).data,
};

// SEO URL Inspection (GSC) — site rollup + drill-down per coverage_state.
export const seoUrlIndexAPI = {
  summary: async () =>
    (await apiClient.get('/api/v1/seo/url-index-summary')).data,
  detail: async (site: string, coverage_state?: string, limit = 200) =>
    (await apiClient.get('/api/v1/seo/url-index-detail',
      { params: { site, coverage_state, limit } })).data,
};

export const ga4API = {
  daily: async (site = 'spoutlore', days = 30) =>
    (await apiClient.get('/api/v1/ga4/daily', { params: { site, days } })).data,
  sources: async (site = 'spoutlore', days = 30, limit = 50) =>
    (await apiClient.get('/api/v1/ga4/sources', { params: { site, days, limit } })).data,
  topPages: async (site = 'spoutlore', days = 30, limit = 50) =>
    (await apiClient.get('/api/v1/ga4/top-pages', { params: { site, days, limit } })).data,
};

export const footerTemplatesAPI = {
  list: async () =>
    (await apiClient.get('/api/v1/megaphone/footer-templates/templates')).data,
  get: async (templateKey: string) =>
    (await apiClient.get(`/api/v1/megaphone/footer-templates/templates/${templateKey}`)).data,
  save: async (templateKey: string, body: {
    name?: string; description?: string;
    footer_body_html: string; cast_block_html: string;
    extra_html?: string; utm_query?: string;
  }) =>
    (await apiClient.put(`/api/v1/megaphone/footer-templates/templates/${templateKey}`, body)).data,
  preview: async (templateKey: string, sample_episode_url?: string, sample_episode_title?: string) =>
    (await apiClient.get(`/api/v1/megaphone/footer-templates/templates/${templateKey}/preview`,
      { params: { sample_episode_url, sample_episode_title } })).data,
  plan: async (templateKey: string, site = 'spoutlore') =>
    (await apiClient.get(`/api/v1/megaphone/footer-templates/templates/${templateKey}/plan`,
      { params: { site } })).data,
  apply: async (templateKey: string, site = 'spoutlore', episode_id?: string) =>
    (await apiClient.post(`/api/v1/megaphone/footer-templates/templates/${templateKey}/apply`,
      null, { params: { site, episode_id } })).data,
};

// Episode draft / publish wizard. Both endpoints are admin-gated.
// preview() is non-destructive (no DB writes); createDraft() inserts a
// fully SEO-baked WP post in 'draft' status.
export interface DraftRequest {
  site: 'spoutlore' | 'mallbrats';
  title: string;
  description: string;
  release_date?: string | null;     // ISO 8601
  season_number?: number | null;
  // Explicit WP 'seasons' CPT post id. Takes precedence over season_number —
  // lets the wizard target NON-numbered pages (Patreon Teasers, Trailers,
  // Episode Drops). Sourced from episodesAPI.listSeasons().
  season_post_id?: number | null;
  episode_number?: number | null;
  content_warning?: string | null;
  extra_body?: string | null;
  auto_publish?: boolean;           // cron flips on release date when true
  // Optional social-card overrides (blank = cascade from SEO title/desc).
  og_title?: string | null;
  og_description?: string | null;
  twitter_title?: string | null;
  twitter_description?: string | null;
}

export interface MegaphonePublishRequest {
  site: 'spoutlore' | 'mallbrats';
  wp_post_id: number;
  title: string;
  summary: string;
  audio_get_url?: string | null;
  artwork_get_url?: string | null;
  pubdate?: string | null;
  season_number?: number | null;
  episode_number?: number | null;
  canonical_url?: string | null;
  media_type?: 'audio' | 'video';
  episode_type?: 'full' | 'trailer' | 'bonus';
  explicit?: 'clean' | 'explicit' | null;
}

export interface MegaphoneVideoPublishRequest {
  site: 'spoutlore' | 'mallbrats';
  wp_post_id: number;
  title: string;
  summary: string;
  video_get_url: string;          // presigned S3 GET URL of the staged .mp4
  pubdate?: string | null;
  season_number?: number | null;
  episode_number?: number | null;
  canonical_url?: string | null;
  content_warning?: string | null;
  explicit?: 'clean' | 'explicit' | null;
  episode_type?: 'full' | 'trailer' | 'bonus';   // bonus drops DAI ad markers
  artwork_get_url?: string | null;                // staged episode COVER (square)
  video_thumbnail_get_url?: string | null;        // staged video POSTER (16:9)
  original_filename?: string | null;
}

export interface MegaphoneVideoJob {
  job_id: string;
  site: string;
  wp_post_id: number;
  title?: string;
  status: 'pending' | 'downloading' | 'uploading' | 'creating' | 'finalizing' | 'done' | 'error';
  progress_pct: number;
  media_file_id: string | null;
  episode_id: string | null;
  cms_edit_url: string | null;
  error: string | null;
  created_at: string;
  finished_at: string | null;
  retry_count?: number;
  retryable?: boolean;
}

export interface YouTubePublishRequest {
  site: 'spoutlore' | 'mallbrats';
  wp_post_id: number;
  title: string;
  description: string;
  video_get_url: string;
  privacy?: 'private' | 'unlisted' | 'public';
  add_to_playlist?: boolean;
  playlist_id?: string | null;
  tags?: string[];
  thumbnail_get_url?: string | null;
  // Two-phase title workflow: upload with clickbait (`title`), then
  // auto-rename to `seo_title` after `seo_rename_days`. If the
  // initial title == seo_title, no rename is scheduled.
  seo_title?: string | null;
  seo_rename_days?: number;
}

export interface YouTubeJob {
  job_id: string;
  site: string;
  wp_post_id: number;
  title?: string;
  status: 'pending' | 'downloading' | 'uploading' | 'linking' | 'done' | 'error';
  progress_pct: number;
  video_id: string | null;
  watch_url: string | null;
  embed_url: string | null;
  privacy: string;
  playlist_id: string | null;
  thumbnail_set: boolean;
  error: string | null;
  created_at: string;
  finished_at: string | null;
  retry_count?: number;
  // True when the backend has the original staged S3 URL persisted AND
  // the job is in a terminal state — so /youtube-retry can replay
  // without the browser re-uploading the (possibly multi-GB) video.
  retryable?: boolean;
}

export interface WpSeason {
  post_id: number;
  title: string;
  status: string;
  season_number: number | null;
  numbered: boolean;
}

export interface PatreonMatch {
  post_id: string;
  title: string;
  url: string;
  published_at: string | null;
  description: string;        // everything before [Content Warning: …]
  content_warning: string;    // comma-separated terms from the bracket
  had_content_warning: boolean;
  suggested_title: string;    // derived 'Patreon Bonus: <Show> Season N, Episode M'
}

export interface PublishActivityItem {
  kind: 'patreon' | 'episode';
  id: string;
  title: string;
  subtitle: string;
  platforms: string[];
  status: 'in_progress' | 'published' | 'error';
  progress_pct: number | null;
  when: string | null;
  views: { label: string; url: string }[];
  resume_job_id: string | null;
  edit_patreon_post_id?: string | null;
  edit_wp_url?: string | null;
  edit_site?: string | null;
  edit_post_id?: number | null;
  thumbnail_url?: string | null;
  error: string | null;
}

export interface MegaphoneCuepoint {
  type: string;            // preroll | midroll | postroll
  start_time: number;
  ad_count: number;
  ad_sources: string[];
  is_active?: boolean;
}

export interface MegaphoneCuepointsResponse {
  site: string;
  megaphone_episode_id: string;
  duration: number | null;
  cuepoints: MegaphoneCuepoint[];
}

export interface PlaceMidrollsResponse {
  ok: boolean;
  status: number;
  before_count: number;
  after_count: number;
  midrolls_added: number;
  cuepoints: MegaphoneCuepoint[];
}

export const episodesAPI = {
  // WP 'seasons' CPT pages for a site — feeds the wizard's season dropdown
  // (numbered seasons + named pages: Patreon Teasers, Trailers, Episode Drops).
  listSeasons: async (site: string): Promise<{ site: string; seasons: WpSeason[] }> =>
    (await apiClient.get('/api/v1/episodes/seasons', { params: { site } })).data,
  // Suggested next season/episode from the show's Megaphone feed (max season,
  // max-episode-in-that-season + 1). has_numbering=false for unnumbered feeds.
  nextNumbering: async (
    site: string,
  ): Promise<{ site: string; has_numbering: boolean; season_number: number | null; episode_number: number | null }> =>
    (await apiClient.get('/api/v1/episodes/next-numbering', { params: { site } })).data,
  // Pull teaser fields from a synced Patreon post (paste URL / id / title).
  pullFromPatreon: async (q: string): Promise<{ query: string; matches: PatreonMatch[] }> =>
    (await apiClient.get('/api/v1/episodes/patreon-post', { params: { q } })).data,
  preview: async (req: DraftRequest) =>
    (await apiClient.post('/api/v1/episodes/preview', req)).data,
  createDraft: async (req: DraftRequest) =>
    (await apiClient.post('/api/v1/episodes/draft', req)).data,
  megaphonePublish: async (req: MegaphonePublishRequest) =>
    (await apiClient.post('/api/v1/episodes/megaphone-publish', req)).data,
  // Programmatic Megaphone VIDEO upload (async job: stage→upload→create).
  megaphoneVideoPublish: async (req: MegaphoneVideoPublishRequest): Promise<MegaphoneVideoJob> =>
    (await apiClient.post('/api/v1/episodes/megaphone-video-publish', req)).data,
  megaphoneVideoJob: async (jobId: string): Promise<MegaphoneVideoJob> =>
    (await apiClient.get(`/api/v1/episodes/megaphone-video-job/${jobId}`)).data,
  megaphoneVideoRetry: async (jobId: string): Promise<MegaphoneVideoJob> =>
    (await apiClient.post(`/api/v1/episodes/megaphone-video-retry/${jobId}`)).data,
  megaphoneAttachAudio: async (req: {
    site: string; wp_post_id: number; audio_get_url: string;
  }) =>
    (await apiClient.post('/api/v1/episodes/megaphone-attach-audio', req)).data,
  sideloadArtwork: async (req: { site: string; wp_post_id: number; artwork_get_url: string }) =>
    (await apiClient.post('/api/v1/episodes/sideload-artwork', req)).data,
  youtubePublish: async (req: YouTubePublishRequest): Promise<YouTubeJob> =>
    (await apiClient.post('/api/v1/episodes/youtube-publish', req)).data,
  youtubePlaylists: async (site: string): Promise<{
    site: string;
    default_playlist_id: string | null;
    playlists: Array<{ id: string; title: string; item_count: number | null }>;
    error?: string;
  }> =>
    (await apiClient.get('/api/v1/episodes/youtube-playlists', { params: { site } })).data,
  youtubeJob: async (jobId: string): Promise<YouTubeJob> =>
    (await apiClient.get(`/api/v1/episodes/youtube-job/${jobId}`)).data,
  youtubeRetry: async (jobId: string): Promise<YouTubeJob> =>
    (await apiClient.post(`/api/v1/episodes/youtube-retry/${jobId}`)).data,
  youtubeJobsForPost: async (wpPostId: number): Promise<{
    wp_post_id: number; jobs: YouTubeJob[];
  }> =>
    (await apiClient.get(`/api/v1/episodes/youtube-jobs-for-post/${wpPostId}`)).data,
  pendingReleases: async (
    params: { site?: string; days_ahead?: number; days_back?: number } = {},
  ) =>
    (await apiClient.get('/api/v1/episodes/pending-releases', { params })).data,
  draftDetail: async (post_id: number, site: string) =>
    (await apiClient.get(`/api/v1/episodes/draft-detail/${post_id}`, { params: { site } })).data,
  publishNow: async (req: {
    site: string; wp_post_id: number; force?: boolean;
    // Forwarded to jeeves so it marks the matching production episode released.
    season_number?: number | null; episode_number?: number | null; title?: string | null;
  }) =>
    (await apiClient.post('/api/v1/episodes/publish-now', req)).data,
  publishedEpisodes: async (params: {
    site?: string; limit?: number; offset?: number;
  } = {}) =>
    (await apiClient.get('/api/v1/episodes/published-episodes', { params })).data,
  // Unified feed of everything published through the app (Patreon + episodes).
  publishActivity: async (limit = 60): Promise<{ items: PublishActivityItem[] }> =>
    (await apiClient.get('/api/v1/episodes/activity', { params: { limit } })).data,
  // ── Midroll ad placement ──
  getMegaphoneCuepoints: async (
    site: string, megaphoneEpisodeId: string,
  ): Promise<MegaphoneCuepointsResponse> =>
    (await apiClient.get(
      `/api/v1/episodes/megaphone-cuepoints/${site}/${megaphoneEpisodeId}`)).data,
  placeMidrolls: async (req: {
    site: string;
    megaphone_episode_id: string;
    midrolls: Array<{ at: string; count?: number; source?: string }>;
  }): Promise<PlaceMidrollsResponse> =>
    (await apiClient.post('/api/v1/episodes/megaphone-place-midrolls', req)).data,
  // Full-replace ALL cuepoints (edit existing pre/mid/post-rolls, not just add).
  setCuepoints: async (req: {
    site: string;
    megaphone_episode_id: string;
    cuepoints: Array<{ type: string; at: string; count?: number; source?: string }>;
  }): Promise<PlaceMidrollsResponse> =>
    (await apiClient.post('/api/v1/episodes/megaphone-set-cuepoints', req)).data,
  // Fetch the episode audio (proxied through our origin) as an object URL the
  // waveform editor can decode. Caller must URL.revokeObjectURL() when done.
  // Object URL of the episode audio for the midroll waveform. Prefers the
  // ORIGINAL uploaded master (sting-free) so offsets line up with the real
  // content; the backend falls back to Megaphone audio when there's no tracked
  // upload. `source` reflects which one was served ('upload' | 'megaphone').
  megaphoneAudioObjectUrl: async (
    site: string, megaphoneEpisodeId: string, preferSource = true,
  ): Promise<{ url: string; source: string }> => {
    const resp = await apiClient.get(
      `/api/v1/episodes/megaphone-audio/${site}/${megaphoneEpisodeId}`,
      { responseType: 'blob', params: preferSource ? { source: 1 } : {} });
    const source = (resp.headers['x-audio-source'] as string) || 'megaphone';
    return { url: URL.createObjectURL(resp.data as Blob), source };
  },
};

// MUM (Megaphone private API for near-real-time downloads + streams)
export const megaphoneMumAPI = {
  status: async () =>
    (await apiClient.get('/api/v1/megaphone/mum/status')).data,
  // Live per-site session liveness (real GraphQL probe via cms-browser-proxy).
  // Shape: { reachable, ok, error, sites: { [slug]: { alive, detail, status, age_s } } }
  tokenHealth: async () =>
    (await apiClient.get('/api/v1/megaphone/mum/token-health')).data,
  setupCookies: async (req: {
    site_slug: 'spoutlore' | 'mallbrats';
    session_id: string;
    cms_web_token: string;
  }) =>
    (await apiClient.post('/api/v1/megaphone/mum/setup-cookies', req)).data,
  runNow: async (site?: string) =>
    (await apiClient.post('/api/v1/megaphone/mum/run-now', null,
      { params: site ? { site } : {} })).data,
  reloginNow: async (site?: string) =>
    (await apiClient.post('/api/v1/megaphone/mum/relogin-now', null,
      { params: site ? { site } : {} })).data,
  timeline: async (site: string, days = 30) =>
    (await apiClient.get('/api/v1/megaphone/mum/timeline',
      { params: { site, days } })).data,
  topEpisodes: async (params: { site?: string; days?: number; limit?: number } = {}) =>
    (await apiClient.get('/api/v1/megaphone/mum/top-episodes', { params })).data,
  todayByEpisode: async (site?: string) =>
    (await apiClient.get('/api/v1/megaphone/mum/today-by-episode',
      { params: site ? { site } : {} })).data,
  episodeTotals: async (episode_ids: string[], days = 90) =>
    (await apiClient.get('/api/v1/megaphone/mum/episode-totals',
      { params: { episode_ids: episode_ids.join(','), days } })).data,
  cookiesDebug: async (site: string) =>
    (await apiClient.get('/api/v1/megaphone/mum/cookies-debug',
      { params: { site } })).data,
};

// Browser-staged file uploads. /presign returns a one-shot PUT URL the
// browser uses to upload directly to S3, plus a long-lived GET URL we
// hand to downstream services (e.g., Megaphone) for async fetch.
export interface PresignRequest {
  site: 'spoutlore' | 'mallbrats';
  kind: 'audio' | 'artwork' | 'video';
  filename: string;
  content_type: string;
}

export interface PresignResponse {
  put_url: string;
  get_url: string;
  object_key: string;
  bucket: string;
  expires_in: number;
}

export const uploadsAPI = {
  presign: async (req: PresignRequest): Promise<PresignResponse> =>
    (await apiClient.post('/api/v1/uploads/presign', req)).data,
  // Upload bytes to S3 via the presigned PUT URL. Reports progress via
  // an optional callback. NOT routed through axios — uses native
  // fetch+XHR to get progress events the browser already provides.
  uploadToS3: (
    putUrl: string, file: File, contentType: string,
    onProgress?: (pct: number) => void,
  ): Promise<void> =>
    new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', putUrl);
      xhr.setRequestHeader('Content-Type', contentType);
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && onProgress) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) resolve();
        else reject(new Error(`S3 PUT failed: ${xhr.status} ${xhr.statusText}`));
      };
      xhr.onerror = () => reject(new Error('S3 PUT network error'));
      xhr.send(file);
    }),

  // ─── Multipart upload (for files > S3's 5 GB single-PUT cap) ──────
  // Chunked PUT with per-part progress aggregation. Concurrency=3
  // saturates a typical home upload pipe without DOS'ing the browser.
  // Aborts the S3 upload on failure so we don't leave orphaned parts
  // counting toward storage. Returns the long-lived GET URL the same
  // way uploadToS3 callers expect from a presign.
  uploadToS3Multipart: async (
    site: 'spoutlore' | 'mallbrats',
    kind: 'audio' | 'artwork' | 'video' | 'short' | 'short-still',
    file: File,
    onProgress?: (pct: number) => void,
    opts?: { partSizeMB?: number; concurrency?: number },
  ): Promise<{ get_url: string; object_key: string; bucket: string }> => {
    const partSize = (opts?.partSizeMB ?? 50) * 1024 * 1024;
    const concurrency = opts?.concurrency ?? 3;
    const totalParts = Math.max(1, Math.ceil(file.size / partSize));
    if (totalParts > 10000) {
      throw new Error(
        `File too large for chosen partSizeMB — would need ${totalParts} ` +
        `parts (max 10,000). Use a larger partSizeMB.`,
      );
    }

    // 1. Initiate multipart
    const init = (await apiClient.post('/api/v1/uploads/multipart/create', {
      site, kind, filename: file.name,
      content_type: file.type || 'application/octet-stream',
    })).data as {
      upload_id: string; object_key: string; bucket: string;
      get_url: string; expires_in: number;
    };

    // Track per-part bytes-uploaded for aggregate progress.
    const partBytes: number[] = new Array(totalParts).fill(0);
    const reportProgress = () => {
      if (!onProgress) return;
      const total = partBytes.reduce((a, b) => a + b, 0);
      onProgress(Math.round((total / file.size) * 100));
    };

    const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

    // One sign+PUT attempt for a part. Rejects on any failure.
    const attemptOnePart = (partNumber: number): Promise<{
      PartNumber: number; ETag: string;
    }> => {
      const start = (partNumber - 1) * partSize;
      const end = Math.min(start + partSize, file.size);
      const blob = file.slice(start, end);
      // Reset this part's progress each attempt so a retry doesn't
      // double-count bytes in the aggregate.
      partBytes[partNumber - 1] = 0;
      reportProgress();
      return (async () => {
        // Re-mint a presigned URL every attempt — a stale/expired URL is a
        // retriable cause of failure on its own.
        const sign = (await apiClient.post('/api/v1/uploads/multipart/sign-part', {
          bucket: init.bucket, object_key: init.object_key,
          upload_id: init.upload_id, part_number: partNumber,
        })).data as { put_url: string };
        // PUT the chunk + read the ETag from the response
        return new Promise<{ PartNumber: number; ETag: string }>((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('PUT', sign.put_url);
          // Guard a truly stuck socket so it can't hang the whole upload
          // forever — the retry loop re-attempts on timeout.
          xhr.timeout = 10 * 60 * 1000; // 10 min per 50MB part
          // No Content-Type header — S3 part PUTs ignore it AND adding it
          // would invalidate the signature unless we also signed it.
          xhr.upload.onprogress = (e) => {
            if (e.lengthComputable) {
              partBytes[partNumber - 1] = e.loaded;
              reportProgress();
            }
          };
          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              // S3 returns ETag with double quotes — keep them; the
              // CompleteMultipartUpload call expects the same string.
              const etag = xhr.getResponseHeader('ETag');
              if (!etag) {
                reject(new Error(
                  `Part ${partNumber}: S3 didn't return ETag header. ` +
                  `Bucket CORS must include ETag in ExposeHeaders.`,
                ));
                return;
              }
              partBytes[partNumber - 1] = end - start;
              reportProgress();
              resolve({ PartNumber: partNumber, ETag: etag });
            } else {
              reject(new Error(
                `Part ${partNumber} PUT failed: ${xhr.status} ${xhr.statusText}`,
              ));
            }
          };
          xhr.onerror = () => reject(new Error(`Part ${partNumber} PUT network error`));
          xhr.ontimeout = () => reject(new Error(`Part ${partNumber} PUT timed out`));
          xhr.send(blob);
        });
      })();
    };

    // Upload a single part WITH RETRIES. Part PUTs are idempotent (re-PUTting
    // the same part number overwrites), so retrying a transient failure —
    // network blip, S3 503 SlowDown, a 429 on sign-part, an expired presign,
    // a momentarily-missing ETag — is safe and is the whole point of
    // multipart. Without this, ONE hiccup among dozens of parts aborted the
    // entire multi-GB upload ("froze partway through").
    const MAX_PART_ATTEMPTS = 5;
    const uploadOnePart = async (partNumber: number) => {
      let lastErr: any;
      for (let attempt = 1; attempt <= MAX_PART_ATTEMPTS; attempt++) {
        try {
          return await attemptOnePart(partNumber);
        } catch (e) {
          lastErr = e;
          if (attempt < MAX_PART_ATTEMPTS) {
            // Exponential backoff with jitter: ~1s, 2s, 4s, 8s.
            const backoff =
              Math.min(8000, 1000 * 2 ** (attempt - 1)) + Math.floor(Math.random() * 400);
            await sleep(backoff);
          }
        }
      }
      // Persistent failure after all retries — clear this part's progress so
      // the failed bytes don't linger in the aggregate, then surface it.
      partBytes[partNumber - 1] = 0;
      reportProgress();
      throw new Error(
        `Part ${partNumber} failed after ${MAX_PART_ATTEMPTS} attempts: ` +
        `${lastErr?.message || lastErr}`,
      );
    };

    // 2. Drive partsfrom 1..totalParts with a small concurrency pool.
    const parts: Array<{ PartNumber: number; ETag: string }> = [];
    let nextPart = 1;
    let aborted = false;

    const worker = async () => {
      while (!aborted) {
        const me = nextPart++;
        if (me > totalParts) return;
        try {
          const result = await uploadOnePart(me);
          parts.push(result);
        } catch (e) {
          aborted = true;
          throw e;
        }
      }
    };

    try {
      await Promise.all(
        Array.from({ length: Math.min(concurrency, totalParts) }, () => worker()),
      );
    } catch (uploadErr) {
      // Best-effort abort so the in-progress storage gets released.
      try {
        await apiClient.post('/api/v1/uploads/multipart/abort', {
          bucket: init.bucket, object_key: init.object_key,
          upload_id: init.upload_id,
        });
      } catch { /* swallow abort errors — original error wins */ }
      throw uploadErr;
    }

    // 3. Complete the upload — parts must be sorted by PartNumber.
    parts.sort((a, b) => a.PartNumber - b.PartNumber);
    const done = (await apiClient.post('/api/v1/uploads/multipart/complete', {
      bucket: init.bucket, object_key: init.object_key,
      upload_id: init.upload_id, parts,
    })).data as { get_url: string; object_key: string; bucket: string };
    if (onProgress) onProgress(100);
    return done;
  },
};

// ─── Shorts pipeline ────────────────────────────────────────────────
// Bulk-ingest vertical clips, give them sequential release slots, and fan
// them out per platform. See docs/SHORTS_PIPELINE_DESIGN.md.
export interface ShortsPlatformCap {
  platform: string;
  implemented: boolean;
  native_schedule: boolean;
  min_seconds: number | null;
  max_seconds: number | null;
  needs_thumbnail: boolean;
  platform_fetches_media: boolean;
}

export interface ShortsTarget {
  target_id: string;
  clip_id: string;
  platform: string;
  status: 'pending' | 'uploading' | 'scheduled' | 'published' | 'error' | 'skipped';
  scheduled_at: string | null;
  native_schedule: boolean;
  platform_post_id: string | null;
  platform_url: string | null;
  attempts: number;
  error: string | null;
  warnings: string[];
  finished_at?: string | null;
  failures?: number;
  last_failure?: string | null;
  missed_slot_at?: string | null;
}

export interface ShortsClip {
  clip_id: string;
  batch_id: string;
  ordinal: number;
  title: string;
  description: string;
  tags: string[];
  filename: string | null;
  bytes: number | null;
  duration_seconds: number | null;
  width: number | null;
  height: number | null;
  scheduled_at: string | null;
  targets: ShortsTarget[];
  /** Latest re-render for this clip, if one was ever queued. */
  render_job?: {
    job_id: string;
    status: 'queued' | 'rendering' | 'done' | 'failed' | 'conflict' | string;
    attempts: number;
    error: string | null;
    requested_at: string | null;
    claimed_at: string | null;
    finished_at: string | null;
  } | null;
}

export interface ShortsBatch {
  batch_id: string;
  site: string;
  label: string;
  wp_post_id: number | null;
  episode_key: string | null;
  source: string;
  created_at: string;
  clips?: ShortsClip[];
  // present on the list endpoint's rollup
  clip_count?: number;
  target_count?: number;
  published_count?: number;
  scheduled_count?: number;
  pending_count?: number;
  error_count?: number;
  skipped_count?: number;
  next_release_at?: string | null;
}

export interface ShortsClipIn {
  bucket: string;
  object_key: string;
  filename?: string;
  title?: string;
  description?: string;
  tags?: string[];
}

/** One cut in the short: a camera, a time range, and that camera's framing. */
export interface ShortsShot {
  start: number;
  end: number;
  camera: string;
  posX: number;
  posY: number;
  scale: number;
  /** false = inherits camera_framing[camera]; true = hand-framed for this cut. */
  custom?: boolean;
  [k: string]: any;
}

/** Word-level transcript. Timings drive the captions, so only text is edited. */
export interface ShortsWord {
  start: number;
  end: number;
  text: string;
  speaker?: string;
  [k: string]: any;
}

export interface ShortsCameraFraming {
  posX: number;
  posY: number;
  scale: number;
  [k: string]: any;
}

/**
 * REAPER's render blob. `[k: string]: any` is load-bearing, not laziness: the
 * editor round-trips this object wholesale, and keys we don't model (`schema`,
 * `project.region_guid`, whatever ReaperMobile adds next) must survive being
 * loaded and sent back or they are erased from the render.
 */
export interface ShortsRenderParams {
  schema?: number;
  title?: string;
  caption?: string;
  shots?: ShortsShot[];
  words?: ShortsWord[];
  camera_framing?: Record<string, ShortsCameraFraming>;
  caption_style?: Record<string, any>;
  render?: Record<string, any>;
  project?: Record<string, any>;
  [k: string]: any;
}

/** A per-shot frame grab of the UNCROPPED source, for framing against. */
export interface ShortsStill {
  shot_index: number;
  camera: string;
  start: number;
  end: number;
  posX: number;
  posY: number;
  scale: number;
  layout?: string;
  url?: string | null;
}

export interface ShortsRenderJob {
  job_id: string;
  clip_id: string;
  status: 'queued' | 'rendering' | 'done' | 'failed' | 'conflict' | string;
  requested_at?: string | null;
  claimed_at?: string | null;
  finished_at?: string | null;
  attempts?: number;
  error?: string | null;
  base_version?: string | null;
  render_params?: ShortsRenderParams | null;
  /** 'render' (default) or 'title_preview'. */
  kind?: string;
  /** Title-preview results. The image is presigned on read. */
  preview_url?: string | null;
  title_lines?: string[] | null;
  title_font_px?: number | null;
  /** false when the box had to shrink below 75% or wrap past 3 lines. */
  fits?: boolean | null;
}

export const shortsAPI = {
  getPlatforms: async (): Promise<{
    platforms: ShortsPlatformCap[];
    defaults: { timezone: string; time_of_day: string; interval_days: number };
  }> => (await apiClient.get('/api/v1/shorts/platforms')).data,

  listBatches: async (limit = 50): Promise<{ batches: ShortsBatch[] }> =>
    (await apiClient.get('/api/v1/shorts/batches', { params: { limit } })).data,

  getBatch: async (batchId: string): Promise<ShortsBatch> =>
    (await apiClient.get(`/api/v1/shorts/batches/${batchId}`)).data,

  createBatch: async (body: {
    site: 'spoutlore' | 'mallbrats';
    label?: string;
    wp_post_id?: number | null;
    episode_key?: string | null;
  }): Promise<{ batch_id: string }> =>
    (await apiClient.post('/api/v1/shorts/batches', body)).data,

  deleteBatch: async (batchId: string): Promise<{ deleted: boolean; already_published_left_live: number }> =>
    (await apiClient.delete(`/api/v1/shorts/batches/${batchId}`)).data,

  // Validates each staged clip with ffprobe BEFORE inserting anything, so a
  // clip that can't be a Short is reported with its reasons and not accepted.
  registerClips: async (
    batchId: string,
    clips: ShortsClipIn[],
    platforms: string[],
  ): Promise<{
    batch_id: string;
    accepted: Array<{
      clip_id: string; ordinal: number; filename: string;
      duration_seconds: number | null; width: number | null; height: number | null;
      skipped_platforms: Record<string, string>;
    }>;
    rejected: Array<{ filename: string; reasons: string[] }>;
    accepted_count: number;
    rejected_count: number;
  }> =>
    (await apiClient.post(`/api/v1/shorts/batches/${batchId}/clips`, { clips, platforms })).data,

  scheduleBatch: async (batchId: string, body: {
    start_date: string;
    time_of_day?: string;
    interval_days?: number;
    timezone?: string;
    platforms?: string[];
    reflow?: boolean;
  }): Promise<{ clips: number; targets_updated: number; targets_unchanged: number }> =>
    (await apiClient.post(`/api/v1/shorts/batches/${batchId}/schedule`, body)).data,

  patchClip: async (clipId: string, body: {
    title?: string; description?: string; tags?: string[]; scheduled_at?: string;
  }): Promise<{
    updated: boolean;
    targets_rescheduled: number;
    /** Platforms whose release time could NOT be moved, and why. */
    targets_not_moved: Record<string, string>;
    clip: ShortsClip;
  }> =>
    (await apiClient.patch(`/api/v1/shorts/clips/${clipId}`, body)).data,

  // Render the caption for every platform exactly as it would post. Pass
  // title/description to preview an unsaved edit.
  previewClip: async (clipId: string, draft?: { title?: string; description?: string }):
    Promise<{
      clip_id: string; title: string;
      episode_url: string | null; episode_url_resolved: boolean;
      previews: Record<string, { post_title: string | null; body: string }>;
    }> =>
    (await apiClient.get(`/api/v1/shorts/clips/${clipId}/preview`,
      { params: draft })).data,

  deleteClip: async (clipId: string): Promise<{ deleted: boolean }> =>
    (await apiClient.delete(`/api/v1/shorts/clips/${clipId}`)).data,

  // Re-streams from the SAME staged S3 object — no re-upload.
  retryTarget: async (targetId: string): Promise<{ target: ShortsTarget }> =>
    (await apiClient.post(`/api/v1/shorts/targets/${targetId}/retry`)).data,

  // Flat calendar rows — one per (clip x platform), which is the unit that
  // actually releases.
  getCalendar: async (daysBack = 60, daysAhead = 120): Promise<{
    targets: Array<{
      target_id: string; platform: string; status: string;
      scheduled_at: string; platform_url: string | null;
      native_schedule: boolean; note: string;
      clip_id: string; title: string; ordinal: number;
      batch_id: string; batch_label: string; site: string;
      episode_key: string | null;
      finished_at: string | null; failures: number;
      last_failure: string | null; missed_slot_at: string | null;
    }>;
    count: number;
  }> =>
    (await apiClient.get('/api/v1/shorts/calendar',
      { params: { days_back: daysBack, days_ahead: daysAhead } })).data,

  // Freshly presigned URLs to WATCH a clip before it goes out. One entry per
  // distinct FILE: Facebook can carry its own 90s cut, so a single player
  // would show a video that is not what Facebook posts.
  getClipMedia: async (clipId: string): Promise<{
    clip_id: string;
    title: string | null;
    thumbnail_url: string | null;
    sources: Array<{
      label: string;
      platforms: string[];
      url: string;
      filename: string | null;
      duration_seconds: number | null;
      width: number | null;
      height: number | null;
    }>;
  }> => (await apiClient.get(`/api/v1/shorts/clips/${clipId}/media`)).data,

  // ── phase 2: framing / transcript editing ────────────────────────────────
  // Everything the editor needs to open a clip. `render_params` comes back
  // VERBATIM, unknown keys included — a render job replaces the region's
  // overrides wholesale, so anything dropped here is erased from the render.
  getRenderParams: async (clipId: string): Promise<{
    clip_id: string;
    title: string | null;
    render_params: ShortsRenderParams | null;
    base_version: string | null;
    stills: ShortsStill[];
    published: string[];
    handed_over: string[];
    editable: boolean;
    needs_force: boolean;
    in_flight_job: ShortsRenderJob | null;
    jobs: ShortsRenderJob[];
  }> => (await apiClient.get(`/api/v1/shorts/clips/${clipId}/render-params`)).data,

  // Lay out the on-screen title box WITHOUT rendering the video (~0.5s on the
  // agent vs ~8 min for a render). Poll getRenderJobs for the result.
  requestTitlePreview: async (clipId: string, body: {
    caption: string;
    caption_style?: Record<string, any> | null;
  }): Promise<ShortsRenderJob> =>
    (await apiClient.post(
      `/api/v1/shorts/clips/${clipId}/request-title-preview`, body)).data,

  // Queue a re-render on the Mac. Send the WHOLE blob you loaded, edited —
  // never a diff. `base_version` is optimistic concurrency: if REAPER
  // re-exported the region in the meantime the job comes back `conflict`
  // without rendering, and hands back its current params so you can reload.
  requestRerender: async (clipId: string, body: {
    render_params: ShortsRenderParams;
    base_version?: string | null;
    force?: boolean;
  }): Promise<ShortsRenderJob> =>
    (await apiClient.post(`/api/v1/shorts/clips/${clipId}/request-rerender`, body)).data,

  getRenderJobs: async (clipId: string): Promise<{ jobs: ShortsRenderJob[] }> =>
    (await apiClient.get(`/api/v1/shorts/clips/${clipId}/render-jobs`)).data,

  getStatus: async (): Promise<{
    counts: Record<string, number>;
    stuck_count: number;
    stuck: Array<{ target_id: string; platform: string; title: string | null; scheduled_at: string | null }>;
  }> => (await apiClient.get('/api/v1/shorts/status')).data,

  // ── thumbnail/title rotation: what's live, what's next, did it swap ──
  getBatchRotation: async (batchId: string): Promise<RotationView> =>
    (await apiClient.get(`/api/v1/shorts/batches/${batchId}/rotation`)).data,

  getEpisodeRotation: async (episodeKey: string): Promise<RotationView> =>
    (await apiClient.get(`/api/v1/shorts/episodes/${episodeKey}/rotation`)).data,

  // Swap one candidate's image for an uploaded one (kind `short-still`).
  replaceCandidate: async (key: string, rank: number, body: {
    bucket: string; object_key: string; filename?: string; person?: string | null;
  }): Promise<{ rank: number; repush: boolean; clip_thumbnail_updated: boolean }> =>
    (await apiClient.post(
      `/api/v1/shorts/clips/${key}/thumbnail-candidates/${rank}/replace`, body)).data,

  // Move upcoming slots and/or change the end, for a clip id or SxxEyy.
  rescheduleRotation: async (key: string, body: RescheduleBody):
    Promise<{ warnings: string[]; changed: unknown; rotation: RotationClip | null }> =>
    (await apiClient.put(`/api/v1/shorts/rotation/${key}/schedule`, body)).data,

  getThumbnailOptions: async (key: string): Promise<ThumbnailOptions> =>
    (await apiClient.get(`/api/v1/shorts/thumbnail-options/${key}`)).data,

  createThumbnailJob: async (key: string, params: ThumbnailJobParams):
    Promise<{ job: ThumbnailJob }> =>
    (await apiClient.post('/api/v1/shorts/thumbnail-jobs', { key, params })).data,

  listThumbnailJobs: async (key: string): Promise<{ jobs: ThumbnailJob[] }> =>
    (await apiClient.get('/api/v1/shorts/thumbnail-jobs', { params: { key } })).data,
};

export type RotationState =
  | 'planned' | 'live' | 'settled' | 'swapping' | 'retrying' | 'failed'
  | 'shown' | 'upcoming' | 'skipped' | 'cut_off';

export interface RotationAttempt {
  requested_at: string | null;
  applied_at: string | null;
  error: string | null;
}

export interface RotationItem {
  rank?: number;
  idx?: number;
  after_days: number | null;
  due_at: string | null;
  state: RotationState;
  live_periods: Array<{ from: string | null; to: string | null }>;
  attempts: RotationAttempt[];
  // thumbnails
  variant_id?: string;
  person?: string | null;
  filename?: string | null;
  image_url?: string | null;
  caption?: string;
  caption_index?: number;
  // titles
  title?: string;
  // set when changed in the dashboard (a re-sent drop keeps these)
  slot_edited_at?: string | null;
  image_replaced_at?: string | null;
}

export interface RescheduleBody {
  thumbnails?: Array<{ rank: number; after_days: number }>;
  titles?: Array<{ idx: number; after_days: number }>;
  end?: { after_days: number | null; settle: string };
}

export interface RotationTimeline {
  live: number | null;
  settled: number | null;
  end_day: number;
  end_at: string | null;
  items: RotationItem[];
}

export interface RotationClip {
  clip_id: string;
  title: string | null;
  video_id: string | null;
  published_at: string | null;
  scheduled_at: string | null;
  settle: string;
  end_after_days: number | null;
  thumbnails: RotationTimeline;
  titles: RotationTimeline | null;
}

export interface RotationView {
  batch_id?: string;
  episode_key?: string;
  max_swap_attempts: number;
  clips: RotationClip[];
}

export interface ThumbnailFace {
  face_id: string;
  image_url: string | null;
  at_seconds: number | null;
  score: number | null;
}

export interface ThumbnailJob {
  job_id: string;
  key: string;
  status: 'queued' | 'rendering' | 'done' | 'failed';
  params: ThumbnailJobParams;
  result?: { replaced?: number[]; added?: number[]; warnings?: string[] } | null;
  error?: string | null;
  requested_at: string | null;
  finished_at?: string | null;
}

export interface ThumbnailJobParams {
  mode: 'people' | 'captions' | 'both' | 'vod';
  picks: Array<{ rank: number; face_ids: string[]; caption_index?: number }>;
  text?: string;
}

export interface ThumbnailOptions {
  key: string;
  people: Array<{ person: string; faces: ThumbnailFace[] }>;
  options: { captions?: string[]; text?: string; [k: string]: unknown };
  scanned_at: string | null;
  candidates: Array<{ rank: number; person: string | null; after_days: number | null;
    caption_index?: number }>;
  modes: string[];
  latest_job: ThumbnailJob | null;
}

export const flightpathAPI = {
  getShows: async () =>
    (await apiClient.get('/api/v1/flightpath/shows')).data,
  getSummary: async (days = 90, show?: string) =>
    (await apiClient.get('/api/v1/flightpath/summary', { params: { days, show } })).data,
  getTimeline: async (days = 90, show?: string) =>
    (await apiClient.get('/api/v1/flightpath/timeline', { params: { days, show } })).data,
  getTopEpisodes: async (days = 90, limit = 20, show?: string) =>
    (await apiClient.get('/api/v1/flightpath/top-episodes', { params: { days, limit, show } })).data,
  getByCountry: async (days = 90, limit = 25, show?: string) =>
    (await apiClient.get('/api/v1/flightpath/by-country', { params: { days, limit, show } })).data,
  getBySlot: async (days = 90, show?: string) =>
    (await apiClient.get('/api/v1/flightpath/by-slot', { params: { days, show } })).data,
  getIngestStatus: async () =>
    (await apiClient.get('/api/v1/flightpath/ingest-status')).data,
};

export const megaphoneAPI = {
  getCalendar: async (past_months = 24) =>
    (await apiClient.get('/api/v1/megaphone/calendar', { params: { past_months } })).data,
  getOverview: async () => (await apiClient.get('/api/v1/megaphone/overview')).data,
  getShows: async () => (await apiClient.get('/api/v1/megaphone/shows')).data,
  getEpisodes: async (params: { show_id?: string; season?: number; limit?: number; offset?: number } = {}) =>
    (await apiClient.get('/api/v1/megaphone/episodes', { params })).data,
  getSeasons: async (showId?: string) => (await apiClient.get('/api/v1/megaphone/seasons', { params: { show_id: showId } })).data,
  getCadence: async (showId?: string) => (await apiClient.get('/api/v1/megaphone/cadence', { params: { show_id: showId } })).data,
  getAdStats: async (showId?: string) => (await apiClient.get('/api/v1/megaphone/ad-stats', { params: { show_id: showId } })).data,
  getDownloads: async (days = 30) => (await apiClient.get('/api/v1/megaphone/downloads', { params: { days } })).data,
  getDemographics: async () => (await apiClient.get('/api/v1/megaphone/demographics')).data,
};

export const patreonAPI = {
  getOverview: async () => (await apiClient.get('/api/v1/patreon/overview')).data,
  getHistory: async (days = 90) => (await apiClient.get('/api/v1/patreon/history', { params: { days } })).data,
  getGrowth: async (bucket: 'day' | 'week' | 'month' = 'month', months_back = 60) =>
    (await apiClient.get('/api/v1/patreon/growth', { params: { bucket, months_back } })).data,
  getStatusBreakdown: async () => (await apiClient.get('/api/v1/patreon/status-breakdown')).data,
  getTiers: async () => (await apiClient.get('/api/v1/patreon/tiers')).data,
  getTopSupporters: async (limit = 25) =>
    (await apiClient.get('/api/v1/patreon/top-supporters', { params: { limit } })).data,
  getMembers: async (limit = 50, offset = 0, status?: string) =>
    (await apiClient.get('/api/v1/patreon/members', { params: { limit, offset, status } })).data,
  getPosts: async (limit = 50, offset = 0, paid_only = false) =>
    (await apiClient.get('/api/v1/patreon/posts', { params: { limit, offset, paid_only } })).data,
  getMrrHistory: async (months_back = 84) =>
    (await apiClient.get('/api/v1/patreon/mrr-history', { params: { months_back } })).data,
  getBenefits: async () =>
    (await apiClient.get('/api/v1/patreon/benefits')).data,
  getDeliverables: async (status?: string, limit = 100, offset = 0) =>
    (await apiClient.get('/api/v1/patreon/deliverables', { params: { status, limit, offset } })).data,
  getSocialReach: async () =>
    (await apiClient.get('/api/v1/patreon/social-reach')).data,
  getCalendar: async (past_months = 24) =>
    (await apiClient.get('/api/v1/patreon/calendar', { params: { past_months } })).data,
};

// ── Patreon POSTING (PAD-#84): create posts via the web JSON:API flow ──
export interface PatreonAccessRule {
  id: string; rule_type: string | null; title: string; amount_cents: number | null;
}
export interface PatreonCollection { id: string; title: string; }
export interface PatreonInventory {
  ok: boolean;
  collections: PatreonCollection[];
  access_rules: PatreonAccessRule[];
  defaults: { audio: string[]; video: string[] };
}
export interface PatreonPostJob {
  job_id: string;
  status: string;          // queued|staging|uploading|creating|finalizing|processing|done|error
  progress_pct: number;
  note?: string | null;    // human-readable progress note (e.g. 'waiting on Patreon video processing')
  post_type: string;
  title: string;
  body?: string | null;
  patreon_post_id: string | null;
  patreon_url: string | null;
  tier_ids: string[];
  tags: string[];
  collection: string | null;
  podcast: string | null;
  is_public: boolean;
  auto_publish: boolean;
  error: string | null;
  created_at: string | null;
  finished_at: string | null;
}
export interface StartPatreonPostReq {
  post_type: string;
  site?: string;
  wp_post_id?: number;
  title?: string;
  body?: string;
  media_source?: 'upload' | 'link' | 'none';
  media_get_url?: string;
  media_file_name?: string;
  link_url?: string;
  podcast?: string;
  collection?: string;
  tier_ids?: string[];
  is_public?: boolean;
  auto_publish?: boolean;
  preview?: { duration_s?: number; start_s?: number };
  tags?: string[];
  tag_context?: { site?: string; show?: string; season_number?: number };
  podcast_meta?: PatreonPodcastMeta;
}

export interface PatreonPodcastMeta {
  episode_type?: string;            // full | trailer | bonus
  episode_number?: number | null;
  season?: number | null;
  is_explicit?: boolean;
}

export interface PatreonSessionHealth {
  ok: boolean; reachable: boolean; expired: boolean;
  authed_as: string | null; user_id: string | null; campaign_id: string | null;
  detail: string | null; reason: string | null; cached?: boolean;
  secret?: { source?: string; captured_at?: string };
}

// Patreon releases staged by the REAPER exporter. Release, retry and discard
// are refused for API tokens: the backend accepts only the dashboard session
// cookie on them, which apiClient sends (withCredentials).
export const patreonReleaseAPI = {
  list: async (): Promise<{ packages: PatreonReleasePackage[] }> =>
    (await apiClient.get('/api/v1/patreon/release-packages')).data,
  get: async (id: string): Promise<PatreonReleasePackage> =>
    (await apiClient.get(`/api/v1/patreon/release-package/${id}`)).data,
  media: async (id: string): Promise<StagedMediaLinks> =>
    (await apiClient.get(`/api/v1/patreon/release-package/${id}/media`)).data,
  release: async (id: string): Promise<PatreonReleasePackage> =>
    (await apiClient.post(`/api/v1/patreon/release-package/${id}/release`)).data,
  retry: async (id: string): Promise<PatreonReleasePackage> =>
    (await apiClient.post(`/api/v1/patreon/release-package/${id}/retry`)).data,
  discard: async (id: string): Promise<{ package_id: string; status: string }> =>
    (await apiClient.delete(`/api/v1/patreon/release-package/${id}`)).data,
};

export const patreonPostAPI = {
  health: async (force = false): Promise<PatreonSessionHealth> =>
    (await apiClient.get('/api/v1/patreon/health', { params: force ? { force: 1 } : {} })).data,
  // Re-capture: persist a freshly-pasted session_id (admin-gated; sends
  // x-admin-token from localStorage, same as Megaphone MUM setup).
  updateSessionCookie: async (session_id: string): Promise<{ saved: boolean; meta: any; health: PatreonSessionHealth }> => {
    const adminToken = localStorage.getItem('adminToken') || '';
    return (await apiClient.post('/api/v1/patreon/session-cookie', { session_id },
      adminToken ? { headers: { 'x-admin-token': adminToken } } : undefined)).data;
  },
  inventory: async (): Promise<PatreonInventory> =>
    (await apiClient.get('/api/v1/patreon/inventory')).data,
  startPost: async (req: StartPatreonPostReq): Promise<PatreonPostJob> =>
    (await apiClient.post('/api/v1/patreon/posts', req)).data,
  getJob: async (jobId: string): Promise<PatreonPostJob> =>
    (await apiClient.get(`/api/v1/patreon/posts/${jobId}`)).data,
  retryJob: async (jobId: string): Promise<PatreonPostJob> =>
    (await apiClient.post(`/api/v1/patreon/posts/${jobId}/retry`)).data,
  listJobs: async (wpPostId: number): Promise<{ jobs: PatreonPostJob[] }> =>
    (await apiClient.get('/api/v1/patreon/posts', { params: { wp_post_id: wpPostId } })).data,

  // ── Ingested back-catalog (admin-gated → send x-admin-token) ──────────────
  catalogCollections: async (): Promise<{ collections: PatreonCatalogFacet[] }> =>
    (await apiClient.get('/api/v1/patreon/catalog/collections', _adminCfg())).data,
  catalogPodcasts: async (): Promise<{ podcasts: PatreonCatalogFacet[] }> =>
    (await apiClient.get('/api/v1/patreon/catalog/podcasts', _adminCfg())).data,
  catalogTags: async (): Promise<{ tags: string[] }> =>
    (await apiClient.get('/api/v1/patreon/catalog/tags', _adminCfg())).data,
  catalogPosts: async (params: {
    collection_id?: string; podcast_id?: string; q?: string;
    include_content?: boolean; limit?: number;
  }): Promise<{ posts: PatreonCatalogPost[]; count: number }> =>
    (await apiClient.get('/api/v1/patreon/catalog/posts', { ..._adminCfg(), params })).data,
  catalogSync: async (): Promise<{ ok: boolean; synced: number }> =>
    (await apiClient.post('/api/v1/patreon/catalog/sync', {}, _adminCfg())).data,
  recentJobs: async (limit = 50): Promise<{ jobs: PatreonPostJob[] }> =>
    (await apiClient.get('/api/v1/patreon/jobs/recent', { ..._adminCfg(), params: { limit } })).data,
  editCatalogPost: async (postId: string, fields: {
    title?: string; body?: string; body_field?: string; tier_ids?: string[];
    tags?: string[]; collection?: string; podcast?: string; is_public?: boolean;
    podcast_meta?: PatreonPodcastMeta;
  }): Promise<{ ok: boolean; post_id: string }> =>
    (await apiClient.post(`/api/v1/patreon/catalog/posts/${postId}/edit`, fields, _adminCfg())).data,
  liveCatalogPost: async (postId: string): Promise<PatreonEditSeed> =>
    (await apiClient.get(`/api/v1/patreon/catalog/posts/${postId}/live`, _adminCfg())).data,
  postAudio: async (postId: string): Promise<{
    audio_url: string | null; media_id: string | null;
    preview_start_s: number | null; preview_duration_s: number | null;
  }> =>
    (await apiClient.get(`/api/v1/patreon/catalog/posts/${postId}/audio`, _adminCfg())).data,
  setPreview: async (postId: string, body: { start_s: number; duration_s: number; media_id?: string | null }):
    Promise<{ ok: boolean; media_id: string }> =>
    (await apiClient.post(`/api/v1/patreon/catalog/posts/${postId}/preview`, body, _adminCfg())).data,
};

function _adminCfg() {
  const adminToken = localStorage.getItem('adminToken') || '';
  return adminToken ? { headers: { 'x-admin-token': adminToken } } : {};
}

export interface PatreonCatalogFacet {
  id: string;
  title: string;
  posts: number;
  latest_at: string | null;
}

// Seed for putting the publish form into edit-an-existing-post mode.
export interface PatreonEditSeed {
  patreon_post_id: string;
  post_type?: string;
  title?: string;
  body?: string | null;
  body_field?: string;
  tags?: string[];
  tier_ids?: string[];
  collection?: string | null;
  podcast?: string | null;
  is_public?: boolean;
  episode_type?: string;
  episode_number?: number | null;
  season?: number | null;
  is_explicit?: boolean;
}

export interface PatreonCatalogPost {
  post_id: string;
  title: string | null;
  teaser_text: string | null;
  content: string | null;
  post_type: string | null;
  url: string | null;
  is_paid: boolean | null;
  published_at: string | null;
  collections: { id: string; title: string }[];
  podcasts?: { id: string; title: string }[];
  tags: string[];
}

export const shopifyAPI = {
  getOverview: async () => (await apiClient.get('/api/v1/shopify/overview')).data,
  getUnfulfilled: async (limit = 50) =>
    (await apiClient.get('/api/v1/shopify/unfulfilled', { params: { limit } })).data,
  getRevenue: async (days = 30) => (await apiClient.get('/api/v1/shopify/revenue', { params: { days } })).data,
  getProducts: async () => (await apiClient.get('/api/v1/shopify/products')).data,
  getOrders: async (limit = 50, offset = 0) => (await apiClient.get('/api/v1/shopify/orders', { params: { limit, offset } })).data,
  getCustomers: async (limit = 50, offset = 0) => (await apiClient.get('/api/v1/shopify/customers', { params: { limit, offset } })).data,
  getProductRevenue: async () => (await apiClient.get('/api/v1/shopify/product-revenue')).data,
  getVariants: async () => (await apiClient.get('/api/v1/shopify/variants')).data,
  getCollections: async () => (await apiClient.get('/api/v1/shopify/collections')).data,
  getTransactions: async (limit = 200) => (await apiClient.get('/api/v1/shopify/transactions', { params: { limit } })).data,
  getRefunds: async () => (await apiClient.get('/api/v1/shopify/refunds')).data,
  getFulfillments: async () => (await apiClient.get('/api/v1/shopify/fulfillments')).data,
};

export const redditAPI = {
  getOverview: async () => (await apiClient.get('/api/v1/reddit/overview')).data,
  getHistory: async (days = 90) => (await apiClient.get('/api/v1/reddit/history', { params: { days } })).data,
  getPosts: async (limit = 50) => (await apiClient.get('/api/v1/reddit/posts', { params: { limit } })).data,
  getTraffic: async (days = 90) => (await apiClient.get('/api/v1/reddit/traffic', { params: { days } })).data,
};

export const tiktokAPI = {
  getOverview: async () => (await apiClient.get('/api/v1/tiktok/overview')).data,
  getHistory: async (days = 90) => (await apiClient.get('/api/v1/tiktok/history', { params: { days } })).data,
  getVideos: async (limit = 50) => (await apiClient.get('/api/v1/tiktok/videos', { params: { limit } })).data,
  getDemographics: async () => (await apiClient.get('/api/v1/tiktok/demographics')).data,
  getContent: async (limit = 50) => (await apiClient.get('/api/v1/tiktok/content', { params: { limit } })).data,
  getCalendar: async (past_months = 12) => (await apiClient.get('/api/v1/tiktok/calendar', { params: { past_months } })).data,
};

export const masterCalendarAPI = {
  get: async (past_months = 6) => (await apiClient.get('/api/v1/dashboard/calendar', { params: { past_months } })).data,
};

export const episodeCorrelationAPI = {
  get: async (limit = 20) =>
    (await apiClient.get('/api/v1/dashboard/episode-correlation', { params: { limit } })).data,
};

export const youtubeRetentionAPI = {
  getHeatmap: async (limit = 24) =>
    (await apiClient.get('/api/v1/dashboard/youtube-retention-heatmap', { params: { limit } })).data,
};

export const youtubeTrafficMixAPI = {
  get: async (weeks = 26) =>
    (await apiClient.get('/api/v1/dashboard/youtube-traffic-mix', { params: { weeks } })).data,
};

export const fanLadderAPI = {
  get: async () =>
    (await apiClient.get('/api/v1/dashboard/fan-ladder')).data,
};

export const podcastListenerCurveAPI = {
  get: async () =>
    (await apiClient.get('/api/v1/dashboard/podcast-listener-curve')).data,
};

export const seoAPI = {
  getSummary: async () =>
    (await apiClient.get('/api/v1/seo/audit-summary')).data,
  getFindings: async (opts: {
    site?: string; kind?: string; severity?: string;
    auto_fixable?: boolean; limit?: number; offset?: number;
  } = {}) =>
    (await apiClient.get('/api/v1/seo/audit-findings', {
      params: {
        site: opts.site, kind: opts.kind, severity: opts.severity,
        auto_fixable: opts.auto_fixable,
        limit: opts.limit ?? 200, offset: opts.offset ?? 0,
      },
    })).data,
  getPageDetail: async (site: string, post_id: number) =>
    (await apiClient.get(`/api/v1/seo/page-detail/${site}/${post_id}`)).data,
  /** Three actionable views over GSC data:
   *  - near_rank: queries ranking 5-20 with impressions (push to top 10)
   *  - low_ctr:   queries with traffic but <2% CTR (rewrite metadesc)
   *  - top_movers: biggest position changes vs prior window
   */
  getKeywordOpportunities: async (opts: {
    site?: string; days?: number; limit?: number;
    near_rank_min?: number; near_rank_max?: number;
    low_ctr_min_impressions?: number; low_ctr_max_ctr?: number;
  } = {}) =>
    (await apiClient.get('/api/v1/seo/keyword-opportunities', {
      params: {
        site: opts.site,
        days: opts.days ?? 28,
        limit: opts.limit ?? 25,
        near_rank_min: opts.near_rank_min,
        near_rank_max: opts.near_rank_max,
        low_ctr_min_impressions: opts.low_ctr_min_impressions,
        low_ctr_max_ctr: opts.low_ctr_max_ctr,
      },
    })).data,
  /** Window-over-window deltas: impressions / clicks / avg position / CTR
   *  vs the prior same-length window. Includes daily series for sparklines. */
  getTrendsSummary: async (opts: { site?: string; days?: number } = {}) =>
    (await apiClient.get('/api/v1/seo/trends-summary', {
      params: { site: opts.site, days: opts.days ?? 28 },
    })).data,
  /** Inbound link summary from Bing Webmaster Tools (the only API-accessible
   *  source — GSC link reports are web-UI only). Returns top referring
   *  domains, top linked pages, plus new/lost links vs the previous snapshot. */
  getBacklinksSummary: async (opts: { site?: string } = {}) =>
    (await apiClient.get('/api/v1/seo/backlinks-summary', {
      params: { site: opts.site },
    })).data,
};

export const metaAPI = {
  getInstagramOverview: async () => (await apiClient.get('/api/v1/meta/instagram/overview')).data,
  getInstagramHistory: async (days = 90) => (await apiClient.get('/api/v1/meta/instagram/history', { params: { days } })).data,
  getInstagramMedia: async (limit = 50) => (await apiClient.get('/api/v1/meta/instagram/media', { params: { limit } })).data,
  getInstagramDemographics: async () => (await apiClient.get('/api/v1/meta/instagram/demographics')).data,
  getFacebookOverview: async () => (await apiClient.get('/api/v1/meta/facebook/overview')).data,
  getFacebookHistory: async (days = 90) => (await apiClient.get('/api/v1/meta/facebook/history', { params: { days } })).data,
  getFacebookPosts: async (limit = 50) => (await apiClient.get('/api/v1/meta/facebook/posts', { params: { limit } })).data,
  getFacebookDemographics: async () => (await apiClient.get('/api/v1/meta/facebook/demographics')).data,
};

export const dataAPI = {
  getInventory: async (schemas?: string, verify: boolean = true) => {
    const response = await apiClient.get('/api/v1/data/inventory', { params: { schemas, verify } });
    return response.data;
  },
  listTables: async (schema: string, includeCounts: boolean = false) => {
    const response = await apiClient.get(`/api/v1/data/tables/${schema}`, { params: { include_counts: includeCounts } });
    return response.data;
  },
  sampleTable: async (schema: string, table: string, limit: number = 50, offset: number = 0) => {
    const response = await apiClient.get(`/api/v1/data/tables/${schema}/${table}`, { params: { limit, offset } });
    return response.data;
  }
}

export default apiClient;
